import { MoveHoldDeciderLine } from "./MoveHoldBanner";
import type { MoveHoldDecider } from "./moveHoldDecider";

/**
 * `TablePausedBanner` — the **table-wide** "a move is paused" notice, for the BYSTANDER: a third
 * connected player, or a GM watching a player-vs-player exchange, who would otherwise see the board
 * simply stop moving mid-turn with no explanation. (The mover's own client shows {@link
 * MoveHoldBanner} off its `pendingMove`; the decider sees the cross-actor `PromptModal`.) Driven by
 * the server's ephemeral, EVERY-client `encounter_move_paused_event` broadcast ({@link
 * import("./remoteEncounterController").RemoteEncounterController.tablePausedMoverId}).
 *
 * **Deliberately suppressed for the two clients who already have a more specific signal**: the host
 * passes `suppress` when this client IS the mover (its own {@link MoveHoldBanner} already covers it)
 * or IS currently holding the live prompt for this exact pause (its `PromptModal`/`SkirmishChooser`
 * flow already covers it) — so nobody ever sees two redundant banners at once.
 *
 * `decider` names who the table is waiting on and why ({@link
 * import("./moveHoldDecider").deriveMoveHoldDecider}), the same line {@link MoveHoldBanner} shows.
 */
export function TablePausedBanner({
  pausedMoverId,
  suppress,
  name,
  decider,
}: {
  /** The mover whose move is table-wide paused, or `null` (nothing paused). */
  pausedMoverId: string | null;
  /** True when THIS client already has a more specific signal for this exact pause. */
  suppress: boolean;
  /** The paused mover's already-visible display name. */
  name: string;
  /** Who is deciding and why, when the server's decision for this pause has landed and is visible here. */
  decider: MoveHoldDecider | null;
}) {
  if (!pausedMoverId || suppress) return null;
  return (
    <div className="combat-reactwait combat-tablepaused" role="status" aria-live="polite">
      <span className="combat-reactwait-spinner" aria-hidden="true" />
      <div className="combat-reactwait-text">
        <span className="combat-reactwait-eyebrow">OMNINET ⁄⁄ Table paused</span>
        <span className="combat-reactwait-reason">
          <b>{name}</b>&apos;s move awaits a reaction…
        </span>
        {decider && <MoveHoldDeciderLine decider={decider} />}
      </div>
    </div>
  );
}
